import { CommonModule } from '@angular/common';
import { Component, HostListener, inject } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { takeUntil } from 'rxjs';
import { ChartHandlerV2 } from 'src/app/classes/widget/chartHandlerV2';
import { extractWeatherAPIIcon, translateWeatherDescription } from 'src/app/classes/widget/weather.constants';
import { WidgetDirective } from 'src/app/components/project-pages/dashboard/widgets/widget.directive';
import { WeatherService } from 'src/app/services/core/weather/weather.service';
import { WidgetLoadingComponent } from 'src/app/components/project-pages/dashboard/widget-loading/widget-loading.component';

@Component({
  selector: 'app-weather',
  imports: [
    WidgetLoadingComponent,
    CommonModule,
    MatIconModule
  ],
  standalone: true,
  templateUrl: './weather.component.html',
  styleUrl: './weather.component.scss'
})

export class WeatherComponent extends WidgetDirective {
  // Services specific to WeatherComponent
  private weatherService: WeatherService = inject(WeatherService);

  // Variables specific for WeatherComponent
  blockValue: any = "";
  tempStyle: string = "";
  cityStyle: string = "";
  descriptionStyle: string = "";
  titlePosition: string = "top";
  chartHandler: ChartHandlerV2 = new ChartHandlerV2();

  displayMode: 'both' | 'current' | 'forecast' = 'both';
  forecastMode: 'hourly' | 'daily' = 'hourly';
  weatherIcon: string = '';
  windDirection: number = 0;
  hourlyForecastData: any[] = [];
  dailyForecastData: any[] = [];
  weatherBackgroundType: string = 'night-rain';

  private scrollAccumulator = 0;
  private isScrolling = false;
  private hourFormats = new Map<number, string>();
  private dayFormats = new Map<number, string>();
  private localData: { temperature: number; city: string;[key: string]: any } | null = null;

  // Scroll horizontal avec la molette sans Shift
  @HostListener('wheel', ['$event'])
  onWheel(event: WheelEvent): void {
    const target = event.target as HTMLElement;
    const carousel = target.closest('.hourly-forecast-carousel, .daily-forecast-carousel') as HTMLElement;

    if (carousel) {
      event.preventDefault();

      this.scrollAccumulator += event.deltaY;

      if (!this.isScrolling) {
        this.smoothScroll(carousel);
      }
    }
  }

  /////////////////////////////////////////////
  // Angular lifecycle hooks parent override //
  /////////////////////////////////////////////

  override ngOnInit(): void {
    super.ngOnInit();
  }

  override ngOnChanges(): void {
    if (this.widget && !this.resize) {
      if (this.localData) {
        this.widgetData = this.localData;
      }

      super.ngOnChanges();

      if (this.localData) {
        this.widgetData = this.localData;
      }
    }
  }

  override ngOnDestroy(): void {
    this.hourFormats.clear();
    this.dayFormats.clear();
    super.ngOnDestroy();
  }

  override updateData(): void {
    this.localData = null;
    this.init();
  }

  init() {
    if (!this.widget || !this.widget.visible || this.resize) return;

    this.widgetDesign = this.widget.design;
    this.widgetQuery = this.widget.query;
    this.type = this.widget.type;
    this.initOptions();

    if (this.localData?.city) {
      this.updateVisualDisplay();
    } else {
      this.fetchWeatherData();
    }
  }

  private smoothScroll(carousel: HTMLElement): void {
    this.isScrolling = true;
    const startTime = Date.now();
    const maxDuration = 500;

    const animate = () => {
      const elapsed = Date.now() - startTime;

      if (Math.abs(this.scrollAccumulator) > 0.5 && elapsed < maxDuration) {
        const scrollStep = this.scrollAccumulator * 0.2;
        carousel.scrollLeft += scrollStep;
        this.scrollAccumulator -= scrollStep;
        requestAnimationFrame(animate);
      } else {
        this.scrollAccumulator = 0;
        this.isScrolling = false;
      }
    };

    requestAnimationFrame(animate);
  }

  updateWeatherData() {
    this.fetchWeatherData();
  }

  initOptions() {
    try {
      let options = this.widgetDesign;

      if (!options) return

      if (options.displayOptions) {
        if (options.displayOptions.showCity)
          this.cityStyle = `font-size:${options.displayOptions.citySize || '29'}px;`;

        if (options.displayOptions.showTemperature)
          this.tempStyle = `font-size:${options.displayOptions.tempSize || '56'}px;`;

        if (options.displayOptions.showDescription)
          this.descriptionStyle = `font-size:${options.displayOptions.descriptionSize || '16'}px;`;
      }

    } catch (e: any) {
      console.error("Error initializing widget options:", e);
    }
  }

  fetchWeatherData() {
    const city = this.widgetDesign?.location.city;

    if (!city) {
      this.error = "No city defined";
      this.widgetData = null;
      return;
    }

    if (this.loading.length > 0) return;

    this.loading.push('weather');
    this.error = undefined;

    const widgetConfig = {
      displayMode: this.displayMode,
      forecastType: this.widgetDesign.forecast?.forecastType,
      daysCount: this.widgetDesign.forecast?.daysCount
    };

    this.weatherService.getWeatherData(city, widgetConfig).pipe(takeUntil(this.destroy$)).subscribe({
      next: (data: any) => {
        this.processWeatherData(data);
        this.updateVisualDisplay();
        this.loading = [];
        this.error = undefined;
      },
      error: (err: any) => {
        console.error('Erreur WeatherAPI:', err);
        this.error = "Erreur de connexion météo";
        this.loading = [];
        this.widgetData = null;
      }
    });
  }

  private updateVisualDisplay(): void {
    if (!this.widgetData) return;

    this.initOptions();
    this.updateWeatherBackground();
    this.cdr.detectChanges();
    this.widgetRendered = true;
  }

  private processWeatherData(data: any) {
    const isCurrentOnly = !data.current;
    const currentData = isCurrentOnly ? data : data.current;
    const locationData = isCurrentOnly ? data.location : data.location;

    this.widgetData = {
      temperature: currentData.temp_c,
      description: translateWeatherDescription(currentData.condition.text),
      city: locationData?.name || 'N/A',
      country: locationData?.country || '',
      humidity: currentData.humidity,
      pressure: currentData.pressure_mb,
      windSpeed: currentData.wind_kph / 3.6, // kph → m/s
      windDirection: currentData.wind_degree,
      icon: extractWeatherAPIIcon(currentData.condition.icon),
      feelsLike: currentData.feelslike_c,
      tempMin: isCurrentOnly ? currentData.temp_c : (data.forecast?.forecastday?.[0]?.day?.mintemp_c || currentData.temp_c),
      tempMax: isCurrentOnly ? currentData.temp_c : (data.forecast?.forecastday?.[0]?.day?.maxtemp_c || currentData.temp_c),
      visibility: currentData.vis_km * 1000, // km → m
      uv: currentData.uv,
      clouds: currentData.cloud,
      precipMm: currentData.precip_mm
    };

    this.localData = this.widgetData;

    this.weatherIcon = currentData.condition.icon.startsWith('//') ? `https:${currentData.condition.icon}` : currentData.condition.icon;
    this.windDirection = currentData.wind_degree;

    if (!isCurrentOnly && data.forecast?.forecastday?.length > 0) {
      this.processHourlyForecast(data.forecast.forecastday);
      this.processDailyForecast(data.forecast.forecastday);
    } else {
      this.hourlyForecastData = [];
      this.dailyForecastData = [];
    }

    // Mettre à jour le background dynamique
    this.updateWeatherBackground();
  }

  processHourlyForecast(forecastdays: any[]): void {
    this.hourlyForecastData = [];
    const now = new Date();
    const currentHour = new Date();
    currentHour.setMinutes(0, 0, 0);

    for (const day of forecastdays.slice(0, 2)) {
      if (!day.hour?.length) continue;

      for (const hour of day.hour) {
        if (this.hourlyForecastData.length >= 24) break;

        const hourTime = new Date(hour.time);

        if (hourTime < currentHour) continue;

        this.hourlyForecastData.push({
          time: hourTime,
          temperature: hour.temp_c,
          icon: extractWeatherAPIIcon(hour.condition.icon),
          iconUrl: hour.condition.icon.startsWith('//') ? `https:${hour.condition.icon}` : hour.condition.icon,
          description: translateWeatherDescription(hour.condition.text),
          humidity: hour.humidity,
          pressure: hour.pressure_mb,
          windSpeed: hour.wind_kph / 3.6,
          precipMm: hour.precip_mm
        });
      }
    }
  }

  processDailyForecast(forecastdays: any[]): void {
    this.dailyForecastData = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    for (const day of forecastdays) {
      if (this.dailyForecastData.length >= 10) break;

      const dayDate = new Date(day.date);
      dayDate.setHours(0, 0, 0, 0);

      if (dayDate.getTime() < today.getTime()) continue;

      this.dailyForecastData.push({
        date: dayDate,
        tempMin: day.day.mintemp_c,
        tempMax: day.day.maxtemp_c,
        icon: extractWeatherAPIIcon(day.day.condition.icon),
        iconUrl: day.day.condition.icon.startsWith('//') ? `https:${day.day.condition.icon}` : day.day.condition.icon,
        description: translateWeatherDescription(day.day.condition.text),
        humidity: day.day.avghumidity,
        windSpeed: day.day.maxwind_kph,
        windDirection: day.hour?.[12]?.wind_degree || 0,
        precipMm: day.day.totalprecip_mm,
        chanceOfRain: day.day.daily_chance_of_rain
      });
    }
  }

  getWeatherIconUrl(iconCode: string): string {
    return iconCode.includes('weatherapi.com') ? (iconCode.startsWith('//') ? `https:${iconCode}` : iconCode) : `https://openweathermap.org/img/wn/${iconCode}@2x.png`;
  }

  getWindDirectionIcon(): string {
    return `rotate(${this.windDirection}deg)`;
  }

  formatWindSpeed(speed: number): string {
    return `${Math.round((speed || 0) * 3.6)} km/h`;
  }

  getRoundedTemp(temp: number): number {
    return Math.round(temp || 0);
  }

  getPrecipitation(): string {
    return this.widgetData?.precipMm ? `${this.widgetData.precipMm} mm` : '0 mm';
  }

  getUVIndex(): string {
    return this.widgetData?.uv?.toString() || 'N/A';
  }

  formatVisibility(visibility: number): string {
    if (!visibility) return 'N/A';

    return visibility >= 1000 ? `${Math.round(visibility / 1000)} km` : `${visibility} m`;
  }

  getHourlyForecast(): any[] {
    return this.hourlyForecastData?.slice(0, 24) || [];
  }

  formatHour(date: Date): string {
    const key = date.getTime();

    if (!this.hourFormats.has(key)) {
      this.hourFormats.set(key, date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }));
    }

    return this.hourFormats.get(key)!;
  }

  trackByHour(index: number, item: any): any {
    return item.time?.getTime() || index;
  }

  getDailyForecast(): any[] {
    return this.dailyForecastData || [];
  }

  formatDay(date: Date): string {
    const key = date.getTime();

    if (!this.dayFormats.has(key)) {
      this.dayFormats.set(key, format(date, 'EEEE d', { locale: fr }));
    }

    return this.dayFormats.get(key)!;
  }

  formatDayShort(date: Date): string {
    return format(date, 'EEE d', { locale: fr });
  }

  getWindDirectionArrow(direction: number): string {
    const arrows = ['↑', '↗', '→', '↘', '↓', '↙', '←', '↖'];
    const index = Math.round(direction / 45) % 8;
    return arrows[index] || '→';
  }

  formatDailyWindSpeed(speed: number): string {
    return `${Math.round(speed || 0)} km/h`;
  }

  trackByDay(index: number, item: any): any {
    return item.date?.getTime() || index;
  }

  getWeatherBackgroundType(condition: string, iconUrl: string): string {
    const conditionLower = condition.toLowerCase();
    const iconCodeMatch = iconUrl.match(/\/(\d+)\.png/);
    const iconCode = iconCodeMatch ? iconCodeMatch[1] : '';
    const isNight = iconUrl.includes('/night/') || iconUrl.includes('n.png');

    if (!iconCode) {
      if (isNight) {
        if (conditionLower.includes('clear')) return 'night-clear';
        if (conditionLower.includes('cloud')) return 'night-cloudy';
        if (conditionLower.includes('rain') || conditionLower.includes('drizzle')) return 'night-rain';

        return 'night';
      }

      if (conditionLower.includes('thunder') || conditionLower.includes('storm')) return 'thunderstorm';
      if (conditionLower.includes('snow') || conditionLower.includes('blizzard')) return 'snow';
      if (conditionLower.includes('rain') || conditionLower.includes('drizzle')) return 'rain';
      if (conditionLower.includes('fog') || conditionLower.includes('mist')) return 'fog';
      if (conditionLower.includes('cloudy')) return 'cloudy';

      return 'sunny';
    }

    if (isNight) {
      if (['113'].includes(iconCode)) return 'night-clear';
      if (['116', '119', '122'].includes(iconCode)) return 'night-cloudy';
      if (['296', '299', '302', '305', '308', '311', '314', '317', '320', '323', '326', '329', '332', '335', '338', '350', '353', '356', '359', '362', '365', '368', '371', '374', '377', '386', '389', '392', '395'].includes(iconCode)) return 'night-rain';
      return 'night';
    }

    if (['200', '201', '202', '210', '211', '212', '221', '230', '231', '232', '386', '389', '392', '395'].includes(iconCode)) {
      return 'thunderstorm';
    }

    if (['227', '230', '323', '326', '329', '332', '335', '338', '368', '371', '374', '377', '392', '395'].includes(iconCode)) {
      return 'snow';
    }

    if (['296', '299', '302', '305', '308', '311', '314', '317', '320', '350', '353', '356', '359', '362', '365', '386', '389'].includes(iconCode)) {
      return 'rain';
    }

    if (['248', '260'].includes(iconCode)) {
      return 'fog';
    }

    if (['116'].includes(iconCode)) return 'partly-cloudy';
    if (['119', '122'].includes(iconCode)) return 'cloudy';

    return 'sunny';
  }

  updateWeatherBackground(): void {
    if (this.widgetData?.description && this.weatherIcon) {
      this.weatherBackgroundType = this.getWeatherBackgroundType(
        this.widgetData.description,
        this.weatherIcon
      );
    }
  }
}